import React, { useState, memo, useMemo } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { Control, useFieldArray, FieldErrors, FieldArrayWithId, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

// Import tipe form utama untuk pengetikan yang kuat
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import CurrencyInputRef from '../../../../shared/components/form/CurrencyInputRef';
import DateInput from '../../../../shared/components/form/DateInput';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { useAuth } from '../../../../auth/context/AuthProvider';

// --- Tipe Props ---

// Props untuk komponen utama
type PaymentSectionPropsV4 = {
    control: Control<CreateBookingFormV4>;
    errors: FieldErrors<CreateBookingFormV4>;
    fields: FieldArrayWithId<CreateBookingFormV4, "bookingroom", "id">[];
};

// Props untuk sub-komponen yang mengelola satu kamar
type SingleRoomPaymentManagerProps = {
    roomIndex: number;
    control: Control<CreateBookingFormV4>;
    errors: FieldErrors<CreateBookingFormV4>;
    accommodationTotal: number;
}

// --- Sub-komponen untuk Mengelola Pembayaran per Kamar ---

const SingleRoomPaymentManager: React.FC<SingleRoomPaymentManagerProps> = ({ roomIndex, control, accommodationTotal }) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: `bookingroom.${roomIndex}.payments`,
    });

    const { hasRole, hasPermission } = useAuth();

    const getInitialDate = () => new Date().toISOString().split('T')[0];
    const [datePayment, setDatePayment] = useState<string>(getInitialDate());
    const [paymentType, setPaymentType] = useState<string>('Cash');
    const [amount, setAmount] = useState<number>(0); // Gunakan number untuk CurrencyInput
    const [surchargePercent, setSurchargePercent] = useState<string>(''); // Gunakan string untuk input desimal

    const handleAddPayment = () => {
        if (!hasRole('dev') && !hasPermission('booking_payment')) {
            toast.error('You do not have permission to add payment');
            return;
        }

        // Validasi Amount (sudah benar)
        if (!amount || amount <= 0) {
            toast.error('Please enter a valid amount greater than zero.');
            return;
        }

        // PERBAIKAN: Tambahkan validasi untuk surcharge
        const surchargeValue = Number(surchargePercent) || 0;
        if (surchargeValue < 0) {
            toast.error('Surcharge percentage cannot be negative.');
            return;
        }

        // Kalkulasi nilai moneter surcharge
        const calculatedSurcharge = surchargeValue;

        append({
            date_payment: datePayment,
            payment_type: paymentType,
            amount: amount,
            surcharge: calculatedSurcharge,
        });

        // Reset state input lokal (sudah benar)
        setDatePayment(getInitialDate());
        setPaymentType('Cash');
        setAmount(0);
        setSurchargePercent('');
        toast.success(`Payment added for Room ${roomIndex + 1}.`);
    };

    const handleRemovePayment = (index: number) => {
        if (!hasRole('dev') && !hasPermission('booking_payment')) {
            toast.error('You do not have permission to remove payment');
            return;
        }
        remove(index);
    };

    const totalPaidForRoom = fields.reduce((acc, payment) => {
        const paymentAmount = Number(payment.amount || 0);
        const paymentSurcharge = Number((payment.amount * payment.surcharge) / 100 || 0);
        return acc + paymentAmount + paymentSurcharge;
    }, 0);

    return (
        <div className="mb-5 p-4 border rounded-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-bold text-primary">Payments for Room {roomIndex + 1}</h5>
                <div className="text-end">
                    <small className="text-muted d-block">Accommodation Total</small>
                    <span className="fw-bold fs-5">{formatPrice(accommodationTotal)}</span>
                </div>
            </div>

            {/* Form Penambahan Pembayaran */}
            <div className="row gy-3 align-items-end mb-4">
                <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label">Payment Date</label>
                    <DateInput className="form-control" value={datePayment} onChange={(e) => setDatePayment(e.target.value)} />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label">Payment Type</label>
                    <select className="form-select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                        <option value="ATM">ATM</option>
                        <option value="Transfer">Bank Transfer</option>
                        <option value="Cash">Cash</option>
                        <option value="Credit Card - AMEX">Credit Card - AMEX</option>
                        <option value="Credit Card - DISCOVER">Credit Card - DISCOVER</option>
                        <option value="Credit Card - MASTERCARD">Credit Card - MASTERCARD</option>
                        <option value="Credit Card - OTHERS">Credit Card - OTHERS</option>
                        <option value="Credit Card - VISA">Credit Card - VISA</option>
                        <option value="Debit Card">Debit Card</option>
                        <option value="DOKU PAYMENT">DOKU PAYMENT</option>
                        <option value="Ota Payment">OTA Payment</option>
                        <option value="PAYPAL">PAYPAL</option>
                    </select>
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Amount</label>
                    <CurrencyInputRef
                        name={`payment_amount_${roomIndex}`}
                        value={amount}
                        onChange={(_e, rawValue) => setAmount(Number(rawValue))}
                    />
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label">Surcharge</label>
                    <div className="input-group">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="0"
                            step="0.01"
                            value={surchargePercent}
                            onChange={(e) => setSurchargePercent(e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                    </div>
                </div>
                <div className="col-12 col-lg-2">
                    <button type='button' className="btn btn-primary w-100" onClick={handleAddPayment}>Add</button>
                </div>
            </div>

            {/* Tabel Daftar Pembayaran */}
            <div className="table-responsive">
                <table className="table table-bordered table-striped mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Payment Date</th>
                            <th>Payment Type</th>
                            <th className='text-end'>Amount</th>
                            <th className='text-end'>Surcharge</th>
                            <th style={{ width: '80px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-muted">No payments added for this room.</td></tr>
                        ) : (
                            fields.map((payment, paymentIndex) => (
                                <tr key={payment.id}>
                                    <td>{payment.date_payment}</td>
                                    <td>{payment.payment_type}</td>
                                    <td className='text-end'>{Number(payment.amount).toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })}</td>
                                    <td className='text-end'>{payment.surcharge}%</td>
                                    <td>
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemovePayment(paymentIndex)}>
                                            <IconTrash size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="table-light fw-bold">
                            <td colSpan={2} className="text-end">Total Paid for this Room:</td>
                            <td colSpan={3} className="text-end fs-5">{formatPrice(totalPaidForRoom)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};


// --- Komponen Utama ---

const PaymentSectionV4: React.FC<PaymentSectionPropsV4> = ({ control, errors, fields }) => {
    const bookingRooms = useWatch({ control, name: "bookingroom" });

    const accommodationTotals = useMemo(() => {
        // Pastikan bookingRooms adalah array sebelum di-map
        if (!Array.isArray(bookingRooms)) return [];

        return bookingRooms.map(room =>
            room?.rates?.reduce((sum, rate) => sum + (Number(rate.rate) || 0), 0) ?? 0
        );
    }, [bookingRooms]);

    return (
        <div className="container-fluid mt-4">
            {fields.map((field, index) => {
                // 3. Ambil total yang sudah dihitung untuk index saat ini
                const accommodationTotalForThisRoom = accommodationTotals[index] || 0;

                return (
                    <SingleRoomPaymentManager
                        key={field.id}
                        roomIndex={index}
                        control={control}
                        errors={errors}
                        // 4. Teruskan total yang sudah dihitung sebagai prop
                        accommodationTotal={accommodationTotalForThisRoom}
                    />
                );
            })}
        </div>
    );
};

export default memo(PaymentSectionV4);